import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /** Resolution of the backdrop brightness sample (see #sampleMedia). */
  static #SAMPLE_COLUMNS = 8;

  static #SAMPLE_ROWS = 64;

  /**
   * Per-image brightness profiles for the backdrop-aware nav, or a marker
   * while one is in flight / after one has failed. Weak so it empties itself
   * when a section is swapped out by the theme editor.
   * @type {WeakMap<Element, Float32Array | 'pending' | 'failed'>}
   */
  #mediaRows = new WeakMap();

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // The backdrop probe samples the middle of the header, so a height change
    // (or a viewport resize reflowing the page) moves what it lands on.
    this.#updateBackdrop();

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      // Before #updateScrollState, which early-returns in the common
      // not-yet-sticky case and would otherwise skip the backdrop entirely.
      this.#updateBackdrop();
      this.#updateScrollState();
    });
  };

  /**
   * Client feedback: the logo should switch between its black and white
   * variants "depending on background". The header is transparent and sits
   * over the page, so what's behind it changes as you scroll and can only be
   * resolved at runtime.
   *
   * Hit-tests the centre of the header, walks past the header's own subtree
   * to whatever is underneath, and classifies that as light or dark. The
   * result goes on data-header-backdrop, which the stylesheet turns into the
   * logo crossfade and nav colour pair.
   */
  #updateBackdrop = () => {
    if (!this.hasAttribute('data-backdrop-aware')) return;

    const rect = this.getBoundingClientRect();
    if (rect.height === 0) return;

    const y = rect.top + rect.height / 2;
    if (y < 0 || y > window.innerHeight) return;

    const backdrop = this.#classifyBackdrop(y);
    if (this.dataset.headerBackdrop !== backdrop) this.dataset.headerBackdrop = backdrop;

    // Releases the transition suppression in the stylesheet, so this first
    // correction is painted rather than animated but every later one moves.
    if (!this.hasAttribute('data-backdrop-resolved')) {
      requestAnimationFrame(() => this.setAttribute('data-backdrop-resolved', ''));
    }
  };

  /**
   * Resolves the section spanning a viewport y and classifies it.
   *
   * Deliberately geometric rather than a document.elementsFromPoint() hit
   * test: the media inside these sections carries pointer-events: none (see
   * the hero's image wrapper), so hit-testing walks straight past the very
   * thing that decides whether the nav needs its light or dark logo and lands
   * on a transparent ancestor instead.
   *
   * @param {number} y - viewport coordinate to sample at
   * @returns {'light' | 'dark'}
   */
  #classifyBackdrop = (y) => {
    // The footer counts: the sticky nav scrolls over it, and its sections
    // live in <footer> rather than under main, so leaving them out left the
    // nav resolving to the light default and going black on the black footer.
    const sections = document.querySelectorAll(
      'main > .shopify-section, #header-group > .shopify-section, footer > .shopify-section'
    );

    for (const section of sections) {
      const rect = section.getBoundingClientRect();
      if (rect.height === 0 || y < rect.top || y > rect.bottom) continue;

      // An explicit choice always wins. Video and tonally mixed photography
      // are judgement calls the sampling below can't make well, so those
      // sections are expected to set this.
      const declared = section.matches('[data-header-contrast]')
        ? section.getAttribute('data-header-contrast')
        : section.querySelector('[data-header-contrast]')?.getAttribute('data-header-contrast');
      if (declared === 'light' || declared === 'dark') return declared;

      // Then the image itself, where there is one covering the nav. This is
      // the "Auto" case and it measures rather than guesses.
      const media = this.#mediaAt(section, y);
      if (media) {
        const rows = this.#rowsFor(media);
        const luminance = rows && this.#stripLuminance(media, rows);
        if (luminance != null) return luminance > 128 ? 'light' : 'dark';
      }

      // Otherwise fall back to the deepest painted background colour in the
      // section, which covers every solid-colour band (the pink CTA, the
      // black footer, the beige body).
      // Restricted to the elements the probe line actually crosses, so a
      // multi-band section (the footer stacks three) is read where the nav
      // is rather than wherever the last painted element happens to be.
      const painted = [section, ...section.querySelectorAll('.section-background, .section')]
        .filter((element) => {
          const box = element.getBoundingClientRect();
          return box.height > 0 && y >= box.top && y <= box.bottom;
        })
        .map((element) => this.#backgroundLuminance(getComputedStyle(element).backgroundColor))
        .filter((luminance) => luminance !== null);

      const luminance = painted.at(-1);
      if (luminance !== undefined) return luminance > 128 ? 'light' : 'dark';

      break;
    }

    // Nothing resolvable: the page background is the pale theme colour.
    return 'light';
  };

  /**
   * The visible image covering a viewport y within a section, if any.
   * @param {Element} section
   * @param {number} y
   * @returns {HTMLImageElement | null}
   */
  #mediaAt = (section, y) => {
    for (const image of section.querySelectorAll('img')) {
      const rect = image.getBoundingClientRect();
      // Zero-size catches display:none, which is how the hero hides whichever
      // of its desktop/mobile art-directed variants isn't in play.
      if (rect.width === 0 || rect.height === 0) continue;
      if (getComputedStyle(image).visibility === 'hidden') continue;
      if (y < rect.top || y > rect.bottom) continue;
      return image;
    }

    return null;
  };

  /**
   * Row-by-row brightness of an image, sampled once and cached.
   *
   * Returns null while the sample is still in flight (or if it failed), which
   * leaves the caller on its background-colour fallback for that frame;
   * #sampleMedia re-runs the update when the numbers land.
   *
   * @param {HTMLImageElement} media
   * @returns {Float32Array | null}
   */
  #rowsFor = (media) => {
    const cached = this.#mediaRows.get(media);
    if (cached) return cached instanceof Float32Array ? cached : null;

    this.#mediaRows.set(media, 'pending');
    this.#sampleMedia(media);
    return null;
  };

  /**
   * Measures an image's brightness profile into #mediaRows.
   *
   * Deliberately re-requests the picture at 64px wide rather than reading the
   * one already on the page: the CDN allows cross-origin reads, but only if
   * the element carrying the image opted in before it loaded. Setting
   * crossOrigin on our own throwaway copy gets a canvas-readable image
   * without adding the attribute to the hero markup, where it would fork the
   * browser cache for the full-size asset. The copy costs about a kilobyte.
   *
   * @param {HTMLImageElement} media
   */
  #sampleMedia = async (media) => {
    try {
      const source = media.currentSrc || media.src;
      if (!source) throw new Error('no source to sample');

      const url = new URL(source, location.href);
      url.searchParams.set('width', String(HeaderComponent.#SAMPLE_COLUMNS * 8));
      // Any crop the page requested would bias the sample; the mapping in
      // #stripLuminance works off the whole image.
      url.searchParams.delete('height');
      url.searchParams.delete('crop');

      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.src = url.href;

      // Waits on the load event rather than decode(): decoding is deferred
      // while the page is in a background tab, which would leave the sample
      // permanently pending and the nav stuck on its fallback colour.
      await new Promise((resolve, reject) => {
        if (probe.complete && probe.naturalWidth > 0) return resolve(undefined);
        probe.addEventListener('load', () => resolve(undefined), { once: true });
        probe.addEventListener('error', () => reject(new Error('probe failed to load')), { once: true });
      });

      if (!probe.naturalWidth) throw new Error('probe decoded empty');

      const cols = HeaderComponent.#SAMPLE_COLUMNS;
      const rowCount = HeaderComponent.#SAMPLE_ROWS;
      const canvas = document.createElement('canvas');
      canvas.width = cols;
      canvas.height = rowCount;

      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) throw new Error('no 2d context');

      context.drawImage(probe, 0, 0, cols, rowCount);
      const { data } = context.getImageData(0, 0, cols, rowCount);

      const rows = new Float32Array(rowCount);
      for (let row = 0; row < rowCount; row++) {
        let total = 0;
        for (let col = 0; col < cols; col++) {
          const i = (row * cols + col) * 4;
          total += ((data[i] ?? 0) * 299 + (data[i + 1] ?? 0) * 587 + (data[i + 2] ?? 0) * 114) / 1000;
        }
        rows[row] = total / cols;
      }

      this.#mediaRows.set(media, rows);
    } catch {
      // Non-Shopify host, a resize the CDN won't serve, or a genuinely opaque
      // image. Marked failed so it's attempted once and never again.
      this.#mediaRows.set(media, 'failed');
    }

    this.#updateBackdrop();
  };

  /**
   * Average brightness of the band of an image that actually sits behind the
   * nav, in that image's own coordinates.
   *
   * Assumes object-fit: cover with the default centred position, which is how
   * every full-bleed section in this theme renders its media: the source is
   * scaled to fill the box and the overflow is cropped evenly, so only part
   * of the image's height is on screen and the nav maps to a band inside it.
   *
   * @param {HTMLImageElement} media
   * @param {Float32Array} rows
   * @returns {number | null}
   */
  #stripLuminance = (media, rows) => {
    const box = media.getBoundingClientRect();
    const { naturalWidth, naturalHeight } = media;
    if (!naturalWidth || !naturalHeight || box.height === 0) return null;

    const scale = Math.max(box.width / naturalWidth, box.height / naturalHeight);
    const visibleHeight = box.height / scale;
    const croppedTop = (naturalHeight - visibleHeight) / 2;
    const lastRow = rows.length - 1;

    const rowAt = (viewportY) => {
      const withinBox = (viewportY - box.top) / box.height;
      const sourceY = croppedTop + withinBox * visibleHeight;
      return Math.min(lastRow, Math.max(0, Math.round((sourceY / naturalHeight) * lastRow)));
    };

    const header = this.getBoundingClientRect();
    const first = rowAt(Math.max(header.top, box.top));
    const last = rowAt(Math.min(header.bottom, box.bottom));
    const from = Math.min(first, last);
    const to = Math.max(first, last);

    let total = 0;
    for (let row = from; row <= to; row++) total += rows[row] ?? 0;

    return total / (to - from + 1);
  };

  /**
   * Perceived brightness (0-255) of a computed background-color, or null when
   * it's transparent enough to see through. Matches the weighting Liquid's
   * `color_brightness` filter uses elsewhere in this section.
   * @param {string} color
   * @returns {number | null}
   */
  #backgroundLuminance = (color) => {
    const parts = color.match(/[\d.]+/g);
    if (!parts || parts.length < 3) return null;

    const alpha = parts.length > 3 ? Number(parts[3]) : 1;
    if (alpha < 0.5) return null;

    const [r = 0, g = 0, b = 0] = parts.map(Number);
    return (r * 299 + g * 587 + b * 114) / 1000;
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }

    if (this.hasAttribute('data-backdrop-aware')) {
      // The listener above is conditional on a sticky mode being set; the
      // backdrop needs to track scrolling either way.
      if (!this.#scrollContainer) {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      this.#updateBackdrop();
      // Re-run once the page has settled: sections above the fold can still
      // be resizing (fonts, images) when this element first connects, which
      // moves what the probe point lands on.
      onDocumentLoaded(() => this.#updateBackdrop());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
